import { CohortFilterGranularity } from '@/api/generated/model';
import { TGranularity } from '@/api/requests/types/charts.type';
import {
  DATE_FORMAT_HUMAN,
  DATE_FORMAT_HUMAN_TIME,
  DATE_FORMAT_HUMAN_WITH_MINUTES,
  DATE_FORMAT_HUMAN_WITH_SECONDS,
  DATE_FORMAT_NUMERIC,
  DATE_FORMAT_TIME_SERIES_DAY,
  DATE_FORMAT_TIME_SERIES_DAY_WITH_YEAR,
  DATE_FORMAT_TIME_SERIES_HOUR,
  DATE_FORMAT_TIME_SERIES_MINUTE,
  DATE_FORMAT_TIME_SERIES_MONTH,
  DATE_FORMAT_TIME_SERIES_YEAR,
} from '@/constants/date.constants';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { Match } from 'effect/index';

dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);

export const formatDateAsTimeFromNow = (date: dayjs.ConfigType): string => {
  return dayjs(date).fromNow();
};

export const formatDateAsTimeFromNowCompact = (date: dayjs.ConfigType): string => {
  const then = dayjs(date);
  const minutes = dayjs().diff(then, 'minute');
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = dayjs().diff(then, 'hour');
  if (hours < 24) return `${hours}h ago`;
  const days = dayjs().diff(then, 'day');
  if (days < 30) return `${days}d ago`;
  return formatDateHuman(then);
};

export const formatDateHuman = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_HUMAN);
};

export const formatDateTMZ = (date?: dayjs.ConfigType): string => {
  return dayjs(date).toISOString();
};

export const formatDateHumanWithSeconds = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_HUMAN_WITH_SECONDS);
};

export const formatDateHumanWithMinutes = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_HUMAN_WITH_MINUTES);
};

export const formatDateHumanTime = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_HUMAN_TIME);
};

export const formatDateNumeric = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_NUMERIC);
};

export const formatDateTimeSeriesMinute = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_TIME_SERIES_MINUTE);
};

export const formatDateTimeSeriesHour = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_TIME_SERIES_HOUR);
};

export const formatDateTimeSeriesDay = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_TIME_SERIES_DAY);
};

export const formatDateTimeSeriesMonth = (date: dayjs.ConfigType): string => {
  return dayjs(date).format(DATE_FORMAT_TIME_SERIES_MONTH);
};

export const chartDateFormatter = (date: dayjs.ConfigType, granularity: TGranularity): string => {
  switch (granularity) {
    case 'hour':
      return formatDateTimeSeriesHour(date);
    case 'month':
      return dayjs(date).format('MMM YYYY');
    case 'week':
      return `${formatDateTimeSeriesDay(dayjs(date))} - ${dayjs(date).add(7, 'day').format(DATE_FORMAT_TIME_SERIES_DAY_WITH_YEAR)}`;
    case 'quarter':
      return `Q${dayjs(date).quarter()} - ${dayjs(date).format('YYYY')} `;
    case 'year':
      return dayjs(date).format(DATE_FORMAT_TIME_SERIES_YEAR);
    default:
      return formatDateHuman(date);
  }
};

export const startOfToday = (): dayjs.Dayjs => {
  return dayjs().startOf('date');
};

export const startOfYesterday = (): dayjs.Dayjs => {
  return dayjs().subtract(1, 'day').startOf('date');
};

export const startOfTheMonth = (): dayjs.Dayjs => {
  return dayjs().startOf('month');
};

/**
 * Truncates a date to the start of its granularity bucket.
 *
 * @example
 * applyGranularity('2026-05-21T15:30:00', 'month') // May 1, 2026 00:00:00
 */
export function applyGranularity(date: dayjs.ConfigType, granularity?: string | null): Date {
  const parsedDate = dayjs(date).toDate();

  return Match.value(granularity).pipe(
    Match.when('minute', () => startOfMinute(parsedDate)),
    Match.when('hour', () => startOfHour(parsedDate)),
    Match.when('day', () => startOfDay(parsedDate)),
    Match.when('week', () => startOfWeek(parsedDate, { weekStartsOn: 1 })),
    Match.when('month', () => startOfMonth(parsedDate)),
    Match.when('quarter', () => startOfQuarter(parsedDate)),
    Match.when('year', () => startOfYear(parsedDate)),
    Match.when(undefined, () => startOfDay(parsedDate)),
    Match.when(null, () => startOfDay(parsedDate)),
    Match.orElse(() => {
      throw new Error('Granularity value not supported');
    }),
  );
}

export const subtractDays = (date: dayjs.ConfigType, days: number): dayjs.Dayjs => {
  return dayjs(date).subtract(days, 'day');
};

export const timestampSecondsToMilliseconds = (timestamp: number): number => {
  return timestamp * 1000;
};

export const timestampMillisecondsToSeconds = (timestamp: number): number => {
  return Math.floor(timestamp / 1000);
};

export const getDaysCount = (start: string, end: string): number => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  return Math.ceil(endDate.diff(startDate, 'd'));
};

export const customDateRangeToHuman = (start: string, end?: string): string => {
  return `${dayjs(start).format(DATE_FORMAT_HUMAN)}-${dayjs(end ?? new Date()).format(DATE_FORMAT_HUMAN)}`;
};

const getTimeZoneOffsetISO = (date: Date) => {
  const offsetMinutes = date.getTimezoneOffset();
  const sign = offsetMinutes > 0 ? '-' : '+';
  const absOffset = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const minutes = String(absOffset % 60).padStart(2, '0');

  return `${sign}${hours}:${minutes}`;
};

export const formatToLocaleDateValue = (date: number): string => {
  // add validation for invalid timestamp
  if (isNaN(date)) {
    throw new Error('Invalid timestamp: NaN is not a valid date value');
  }

  const localDate = new Date(date);

  // also validate the resulting Date object
  if (isNaN(localDate.getTime())) {
    throw new Error('Invalid timestamp: Results in invalid Date object');
  }

  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');
  const seconds = String(localDate.getSeconds()).padStart(2, '0');
  const timeZoneString = getTimeZoneOffsetISO(localDate);
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}${timeZoneString}`;
};

export const getLocalizedDatePlaceholder = (locale?: string): string => {
  const resolvedLocale = locale ?? Intl.DateTimeFormat().resolvedOptions().locale;
  const isItalian = resolvedLocale.toLowerCase().startsWith('it');
  const dayToken = isItalian ? 'gg' : 'dd';
  const monthToken = 'mm';
  const yearToken = isItalian ? 'aaaa' : 'yyyy';

  return new Intl.DateTimeFormat(resolvedLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .formatToParts(new Date(2000, 0, 15))
    .map((part) => {
      switch (part.type) {
        case 'day':
          return dayToken;
        case 'month':
          return monthToken;
        case 'year':
          return yearToken;
        default:
          return part.value;
      }
    })
    .join('');
};

export const formatToHuman = (date: number): string => {
  return dayjs(date).format(DATE_FORMAT_HUMAN);
};

/**
 *
 * given a date string in ISO format,
 * returns a string representing the date range
 *
 * @param isoDate
 * @param granularity
 * @param considerLocalTimezone
 */
export const getDateRangeStringByGranularity = (
  isoDate: string,
  granularity: CohortFilterGranularity,
  considerLocalTimezone = true,
) => {
  if (!isValidDate(isoDate)) {
    throw new Error('Invalid date string');
  }

  const dateObj = new Date(isoDate);

  let daysToAdd: number;
  switch (granularity) {
    case 'week':
      daysToAdd = 7;
      break;
    case 'month':
      daysToAdd = 30;
      break;
    default:
      daysToAdd = 1;
      break;
  }

  if (granularity === 'day') {
    return `${formatDateString(isoDate, considerLocalTimezone)}`;
  }

  dateObj.setDate(dateObj.getDate() + daysToAdd - 1);
  return `${formatDateString(isoDate, considerLocalTimezone)} - ${formatDateString(dateObj.toISOString(), considerLocalTimezone)}`;
};

export function stripTimezone(datetimeStr: string) {
  return datetimeStr.replace(/([+-]\d{2}:\d{2}|Z)$/, '');
}

export function isValidDate(date: string): boolean {
  // Regex to validate ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ or ±HH:MM)
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
  if (!isoDateRegex.test(date)) {
    return false;
  }

  // Parse date
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) {
    return false; // Invalid date
  }

  // Extract components from the input string
  const [year, month, day] = date.split('T')[0].split('-').map(Number);

  // **Leap Year Validation**
  if (month === 2 && day === 29) {
    // Check if the year is a valid leap year
    if (!(year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0))) {
      return false; // Not a leap year, Feb 29 is invalid
    }
  }

  return true;
}

export const formatDateString = (isoDate: string, considerLocalTimezone = true): string => {
  if (!isValidDate(isoDate)) {
    throw new Error('Invalid date string');
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (considerLocalTimezone) {
    const date = new Date(isoDate);

    // ensure the date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date string');
    }

    const day = date.getDate(); // consider local timezone day
    const monthIndex = date.getMonth(); // consider local timezone month
    const month = months[monthIndex];

    return `${day} ${month}`;
  }

  // ELSE
  const dateParts = isoDate.split('T')[0].split('-');
  const day = dateParts[2];
  const monthIndex = parseInt(dateParts[1], 10) - 1;
  const month = months[monthIndex];

  return `${day} ${month}`;
};

export const formatDateObject = (inputDate: Date): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const day = inputDate.getDate().toString().padStart(2, '0');
  const month = months[inputDate.getMonth()];

  return `${day} ${month}`;
};

// Year-inclusive variant for table row labels where the time range context is not visible.
export function applyGranularityToTableLabel(label: string, granularity?: TGranularity): string {
  const format =
    granularity === 'day'
      ? 'MMM DD, YYYY'
      : granularity === 'hour'
        ? 'MMM DD YYYY, ha'
        : granularity === 'week'
          ? 'MMM DD, YYYY'
          : granularity === 'month'
            ? 'MMM YYYY'
            : DATE_FORMAT_TIME_SERIES_YEAR;

  return dayjs(label).format(format);
}

export const timezoneOffsetMinutes = () => new Date().getTimezoneOffset();

export const getTimezoneName = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Calculate the number of days up to now
export const getDaysUpToNow = (startDate: Date) => {
  const endDate = dayjs();
  return endDate.diff(dayjs(startDate), 'day');
};
